import { authenticationMethod, environment } from '@/helpers/Environment';
import { withSellerPrefix } from '@/helpers/route';
import { apiErrorMessageOrReturn } from '@/errors/helpers';
import { loginAndTransferCart } from '@/state/Cart';
import { flags } from '@/store/Flags';
const loginPath = '/auth0-login';
const logoutPath = '/auth0-logout';
function isAuth0() {
    return authenticationMethod === 'auth0_ecomm' && navigator.cookieEnabled;
}
let auth0Client = undefined;
function getAuth0ClientOptions() {
    const options = environment.web.auth0_login;
    if (!options)
        return;
    const clientOptions = {
        domain: options.domain,
        audience: options.audience,
        client_id: options.clientId,
        redirect_uri: window.location.origin + loginPath,
        // @see https://ticketure-team.atlassian.net/browse/TIC-6544
        ...(flags.auth0_session_revocation && {
            useRefreshTokens: true,
            cacheLocation: 'localstorage',
        }),
    };
    return clientOptions;
}
async function getAuth0Client() {
    if (auth0Client)
        return auth0Client;
    const options = getAuth0ClientOptions();
    if (options && navigator.cookieEnabled) {
        // Lazy load auth0. Most tenants don't use it, but it makes up ~10% of the vendor bundle.
        const { default: createAuth0Client } = await importAuth0Sdk();
        // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
        auth0Client = await createAuth0Client(options);
        return auth0Client;
    }
}
async function importAuth0Sdk() {
    if (window.Cypress && window.__mockAuth0Import) {
        return window.__mockAuth0Import();
    }
    return await import('@auth0/auth0-spa-js');
}
async function isSessionRevoked() {
    try {
        const client = await getAuth0Client();
        if (!client)
            return false;
        // createAuth0Client() runs checkSession() → getTokenSilently({ ignoreCache: false }), which can return a cached access token without contacting Auth0. Force a network check so a revoked refresh token is detected.
        await client.getTokenSilently({ ignoreCache: true });
        return false;
    }
    catch {
        // cleanup the cache (cookies and local storage)
        let client = auth0Client;
        if (!client) {
            // create a temp client just so we can gracefully cleanup the cache
            const options = getAuth0ClientOptions();
            if (options && navigator.cookieEnabled) {
                const { Auth0Client } = await importAuth0Sdk();
                client = new Auth0Client({
                    ...options,
                    useRefreshTokens: true,
                    cacheLocation: 'localstorage',
                });
            }
        }
        client?.logout({ localOnly: true });
        return true;
    }
}
async function login(currentPath) {
    const client = await getAuth0Client();
    if (!client)
        return { success: false, error: Error('Auth0 client not initialized') };
    // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
    try {
        await client.loginWithRedirect({
            redirect_uri: window.location.origin + loginPath,
            appState: { fullPath: withSellerPrefix(currentPath) },
        });
        return { success: false, error: Error('Auth0 redirect failed') };
    }
    catch (e) {
        return { success: false, error: e };
    }
}
/**
 * Navigate after Auth0 login. Production uses a full navigation so the router re-initializes with the seller prefix.
 * E2E sets `window.Cypress` (Playwright via `markWebClientTestContext`) and uses `history.pushState` plus Vue Router
 * so tests follow a production-like path without reloading (Auth0 stubs stay on `window`).
 */
function navigateAfterAuth0Login(fullPath) {
    if (window.Cypress) {
        window.history.pushState(null, '', fullPath);
        const router = window.vueApp
            ?.$router;
        if (router) {
            const slug = environment.seller?.slug;
            let routerPath = fullPath;
            if (slug) {
                const prefix = `/${slug}`;
                if (fullPath === prefix ||
                    fullPath.startsWith(`${prefix}/`) ||
                    fullPath.startsWith(`${prefix}?`) ||
                    fullPath.startsWith(`${prefix}#`)) {
                    routerPath = fullPath.slice(prefix.length) || '/';
                    if (!routerPath.startsWith('/')) {
                        routerPath = `/${routerPath}`;
                    }
                }
            }
            void router.push(routerPath);
        }
        return;
    }
    // See https://tixtrackteam.slack.com/archives/C02CMPTFLKF/p1748219412509819?thread_ts=1748212393.729409&cid=C02CMPTFLKF
    window.location.assign(fullPath);
}
async function loginToTicketure() {
    try {
        const client = await getAuth0Client();
        const claims = await client?.getIdTokenClaims();
        if (!claims)
            return { success: false, error: Error('Auth0 token not present') };
        await loginAndTransferCart({ jwt: claims.__raw });
        return { success: true };
    }
    catch (e) {
        return { success: false, error: e };
    }
}
async function handleRedirectCallback() {
    if (!isAuth0())
        return { success: false, error: Error('Auth0 is not enabled') };
    const client = await getAuth0Client();
    if (!client) {
        return {
            success: false,
            error: Error('Auth0 could not be initialized'),
        };
    }
    try {
        // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
        const { appState } = await client.handleRedirectCallback();
        const { success, error } = await loginToTicketure();
        if (success) {
            navigateAfterAuth0Login(appState.fullPath);
            return { success: true };
        }
        if (error)
            throw error;
        return { success: false, error: Error('Login failed') };
    }
    catch (e) {
        if (e instanceof Error && e.message === 'Invalid state') {
            // User probably navigated here using browser forward/back button.
            window.history.forward();
            // Log the error if the browser is still on this page after 5 seconds.
            // That probably means there is no location in the history API to navigate forward to.
            setTimeout(() => {
                throw e;
            }, 5000);
            return { success: false, error: e };
        }
        else {
            const { error } = apiErrorMessageOrReturn(e);
            return { success: false, error: error };
        }
    }
}
async function logout(returnTo) {
    const client = await getAuth0Client();
    if (!client)
        return { success: false, error: Error('Auth0 client not initialized') };
    // @see https://auth0.com/docs/quickstart/spa/vuejs/01-login#create-an-authentication-wrapper
    client.logout({ returnTo });
    return { success: true };
}
export const auth0 = {
    isAuth0,
    login,
    handleRedirectCallback,
    logout,
    loginPath,
    logoutPath,
    isSessionRevoked,
};
